import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfigStore, waitForConfig } from '../../stores/configStore';
import { useLocationStore, isMapConsentRequired } from '../../stores/locationStore';
import { useAuthStore } from '../../stores/authStore';
import { useBatteryStore } from '../../stores/batteryStore';
import { useUIStore } from '../../stores/uiStore';
import { useThemeStore, THEMES } from '../../stores/themeStore';
import { updateMyLocation, goOffline } from '../../services/locationService';
import { subscribeUnreadCount } from '../../services/dmService';
import { IconRadio, IconRadar, IconMessage, IconBoard, IconUser, IconSettings } from '../../components/icons';
import { colors } from '../../styles/colors';
import UserProfileModal from '../../components/UserProfileModal';
import UserContextMenu from '../../components/UserContextMenu';
import DMComposeModal from '../../components/DMComposeModal';
import ReportModal from '../../components/ReportModal';
import DropDetailModal from '../../components/DropDetailModal';
import NoticePopup from '../../components/NoticePopup';
import GoogleProfileSetup from '../../components/GoogleProfileSetup';
import LandscapeGuard from '../../components/LandscapeGuard';
import LocationConsentModal from '../../components/LocationConsentModal';
import MapLocationConsentModal from '../../components/MapLocationConsentModal';

const tabs: { to: string; label: string; icon: (p: { color: string }) => ReactNode }[] = [
  { to: '/app', label: 'radio.title', icon: ({ color }) => <IconRadio color={color} /> },
  { to: '/nearby', label: 'nearby.title', icon: ({ color }) => <IconRadar color={color} /> },
  { to: '/dm', label: 'dm.title', icon: ({ color }) => <IconMessage color={color} /> },
  { to: '/board', label: 'board.title', icon: ({ color }) => <IconBoard color={color} /> },
  { to: '/profile', label: 'profile.title', icon: ({ color }) => <IconUser color={color} /> },
  { to: '/settings', label: 'settings.title', icon: ({ color }) => <IconSettings color={color} /> },
];

export default function AppLayout() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  // Apply per-tab font size on route change
  useEffect(() => {
    const tab = '/' + (location.pathname.split('/')[1] || '');
    useUIStore.getState().setCurrentTab(tab);
  }, [location.pathname]);
  const subscribe = useConfigStore((s) => s.subscribe);
  const startWatching = useLocationStore((s) => s.startWatching);
  const syncIntervalSec = useLocationStore((s) => s.syncIntervalSec);
  const user = useAuthStore((s) => s.user);
  const cells = useBatteryStore((s) => s.cells);
  const maxCells = useBatteryStore((s) => s.maxCells);
  const pct = maxCells > 0 ? Math.floor((cells / maxCells) * 100) : 0;
  const isOvercharged = pct > 100;
  const batteryColor = isOvercharged ? '#00e5ff' : pct > 50 ? colors.batteryHigh : pct > 20 ? colors.batteryMedium : colors.batteryLow;
  const filledBars = Math.ceil((pct / 100) * 4);

  const chatTheme = useThemeStore((s) => s.chatTheme);
  const currentTheme = THEMES.find(t => t.key === chatTheme) ?? THEMES[0];
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);

  // Capacitor: detect background/foreground state change immediately
  useEffect(() => {
    if (!user) return;
    import('@capacitor/app').then(({ App: CapApp }) => {
      CapApp.addListener('appStateChange', ({ isActive }) => {
        const uid = useAuthStore.getState().user?.uid;
        if (!uid) return;
        if (!isActive) {
          // App went to background → immediately mark offline
          goOffline(uid).catch(() => {});
        } else {
          // App came to foreground → sync location immediately
          const loc = useLocationStore.getState().effectiveLocation;
          if (loc) {
            const u = useAuthStore.getState().user;
            if (u) {
              const share = useLocationStore.getState().shareLocationOnMap;
              updateMyLocation(u.uid, loc.lat, loc.lng, u.nickname, u.gender, u.profileImageUrl, share).catch(() => {});
            }
          }
        }
      });
    }).catch(() => {}); // Not a native platform
  }, [user?.uid]);

  // Set #root height to actual visible viewport (excludes mobile browser chrome)
  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    const update = () => {
      const h = window.visualViewport?.height ?? window.innerHeight;
      root.style.height = h + 'px';
    };
    update();
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
      return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); };
    } else {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
  }, []);

  // === Modal states (driven by custom events from RadioPage context menu, NearbyPage, etc.) ===
  const [profileModal, setProfileModal] = useState<{ uid: string; nickname: string } | null>(null);
  const [dmCompose, setDmCompose] = useState<{ recipientId: string; recipientNickname: string } | null>(null);
  const [reportModal, setReportModal] = useState<{ targetId: string; targetNickname: string; contentType?: string; contentPreview?: string; contentTimestamp?: number } | null>(null);
  const [dropDetailId, setDropDetailId] = useState<string | null>(null);
  const [infoPost, setInfoPost] = useState<any>(null);
  const [showGoogleSetup, setShowGoogleSetup] = useState(false);
  const [showLocationConsent, setShowLocationConsent] = useState(false);
  const [showMapLocationConsent, setShowMapLocationConsent] = useState(false);

  // Force profile setup if not done (Google accounts, or any account missing setup)
  useEffect(() => {
    if (!user) { setShowGoogleSetup(false); return; }
    if (user.profileSetupDone === false) {
      setShowGoogleSetup(true);
    } else {
      setShowGoogleSetup(false);
    }
  }, [user?.uid, user?.profileSetupDone]);

  /**
   * Show the Apple-5.1.2 map-visibility prompt, but only after the remote config has
   * arrived (or its 5s timeout elapsed). `mapConsentRequiredPlatforms` lets an admin
   * turn the whole flow off per platform without a rebuild; reading it before the
   * config loads would always see the safe default (`required`).
   */
  const maybeAskMapConsent = () => {
    waitForConfig().then(() => {
      useLocationStore.getState().rehydrateShareLocationOnMap();
      if (!isMapConsentRequired()) return;
      const s = useLocationStore.getState();
      if (!s.shareLocationPrompted && !s.shareLocationOnMap && !s.dontAskMapConsent) {
        setShowMapLocationConsent(true);
      }
    });
  };

  useEffect(() => {
    const unsubConfig = subscribe();
    let unsubLocation: (() => void) | null = null;

    // Session-scoped state (shareLocationPrompted, shareLocationOnMap) lives in
    // sessionStorage, which the browser / Capacitor WebView auto-clears on tab close
    // or process kill but PERSISTS across refresh and background→foreground. That's
    // exactly the desired semantics, so we DO NOT call resetShareLocationOnMap here.
    // Explicit logins trigger a reset via writeSession (authStore).

    // Check permissions on mount, then decide whether to show modal or start watching
    useLocationStore.getState().checkPermission().then(() => {
      const store = useLocationStore.getState();
      const state = store.permissionState;
      let optedOut = store.userOptedOut;

      // If OS permission is granted, the user has implicitly opted in at the OS level —
      // clear any stale in-app userOptedOut flag (set by declining the old
      // LocationConsentModal once). Otherwise a stuck optedOut=true would skip both
      // branches and leave location unwatched + map consent popup never shown.
      if (state === 'granted' && optedOut) {
        store.setUserOptedOut(false);
        optedOut = false;
      }

      if (state === 'granted') {
        // OS already granted → start watching silently
        unsubLocation = startWatching();
        // Apple 5.1.2 — ask each session whether to display my location on the map.
        // Skip on platforms where admin has disabled the strict consent flow, or when the
        // user has already enabled sharing or checked "don't ask again" on this device.
        // MUST wait for the remote config: isMapConsentRequired() defaults to `true`
        // until Firestore answers, so deciding here would flash the modal on platforms
        // where the flow is remotely turned off.
        maybeAskMapConsent();
      } else if ((state === 'prompt' || state === 'unknown') && !optedOut) {
        // First-time ask: show custom consent modal before any system prompt
        setShowLocationConsent(true);
      }
      // If 'denied' → do nothing. App continues without location.
    });

    return () => {
      unsubConfig();
      unsubLocation?.();
    };
  }, [subscribe, startWatching]);

  const config = useConfigStore((s) => s.config);
  const configLoaded = useConfigStore((s) => s.loaded);
  useEffect(() => {
    if (user) {
      useBatteryStore.getState().setCells(user.battery);
      const max = config.signupDefaultCells || 1_000_000;
      useBatteryStore.getState().setMaxCells(max);
    }
  }, [user?.battery, config.signupDefaultCells]);

  // After server config loads, rehydrate shareLocationOnMap so consent-not-required platforms
  // pick up the user's persisted preference (default ON) instead of the session-default (false).
  useEffect(() => {
    if (configLoaded) useLocationStore.getState().rehydrateShareLocationOnMap();
  }, [configLoaded, config.mapConsentRequiredPlatforms?.web, config.mapConsentRequiredPlatforms?.android, config.mapConsentRequiredPlatforms?.ios]);

  // Auto charge while connected
  const autoChargeRunning = useRef(false);
  useEffect(() => {
    if (!user) return;

    // Wait for config to be loaded from Firestore before running auto-charge
    const configLoaded = useConfigStore.getState().loaded;
    if (!configLoaded) return;

    const check = async () => {
      if (autoChargeRunning.current) return;
      const cfg = useConfigStore.getState().config;
      const intervalMin = cfg.autoChargeIntervalMinutes;
      const chargeAmount = cfg.autoChargeAmount ?? 0;
      // Disabled if interval is 0 or charge amount is 0
      if (intervalMin <= 0 || chargeAmount <= 0) return;

      const u = useAuthStore.getState().user;
      if (!u) return;

      // Skip if no lastCellChargeAt (new user — don't auto-charge on first login)
      const lca = u.lastCellChargeAt;
      if (!lca) {
        // Set initial timestamp so future charges work
        try {
          const { Timestamp, doc: docRef, updateDoc } = await import('firebase/firestore');
          const { db: fireDb } = await import('../../lib/firebase/app');
          await updateDoc(docRef(fireDb, 'users', u.uid), { lastCellChargeAt: Timestamp.now() });
        } catch {}
        return;
      }

      let lastMs = 0;
      if ((lca as any).toMillis) lastMs = (lca as any).toMillis();
      else if ((lca as any).seconds) lastMs = (lca as any).seconds * 1000;
      const nextMs = lastMs + intervalMin * 60000;
      if (Date.now() < nextMs) return;

      autoChargeRunning.current = true;
      try {
        const max = cfg.signupDefaultCells || 1_000_000;
        const cur = useBatteryStore.getState().cells;
        const { Timestamp, doc: docRef, updateDoc } = await import('firebase/firestore');
        const { db: fireDb } = await import('../../lib/firebase/app');
        const nowTs = Timestamp.now();
        if (cur >= max) {
          await updateDoc(docRef(fireDb, 'users', u.uid), { lastCellChargeAt: nowTs });
          return;
        }
        const actual = Math.min(chargeAmount, max - cur);
        await useBatteryStore.getState().charge(u.uid, actual, 'AUTO_CHARGE');
        await updateDoc(docRef(fireDb, 'users', u.uid), { lastCellChargeAt: nowTs });
      } catch (err) {
        console.error('[AutoCharge] Failed:', err);
      } finally {
        autoChargeRunning.current = false;
      }
    };

    check(); // Run immediately on mount
    const iv = setInterval(check, 10_000);
    return () => clearInterval(iv);
  }, [user?.uid]);

  // Sync location + connection notification
  // Persist last notification location across refreshes to avoid duplicate notifications
  const lastNotifLocation = useRef<{ lat: number; lng: number } | null>(
    (() => { try { const s = sessionStorage.getItem('ourhertz_last_notif_loc'); return s ? JSON.parse(s) : null; } catch { return null; } })()
  );

  useEffect(() => {
    if (!user) return;
    const loc = useLocationStore.getState().effectiveLocation;

    const syncLocation = () => {
      const current = useLocationStore.getState().effectiveLocation;
      if (!current) return;
      // Always read the latest user from store to avoid stale profileImageUrl
      const latestUser = useAuthStore.getState().user;
      if (!latestUser) return;
      const share = useLocationStore.getState().shareLocationOnMap;
      updateMyLocation(
        latestUser.uid, current.lat, current.lng,
        latestUser.nickname, latestUser.gender, latestUser.profileImageUrl,
        share,
      ).catch(() => {});

      // Connection notification logic
      if (latestUser.connectionNotificationEnabled !== false) {
        const threshold = latestUser.connectionNotificationDistanceThreshold ?? 1000;
        let shouldSend = false;

        if (!lastNotifLocation.current) {
          // First time ever (no previous notification location stored)
          shouldSend = true;
        } else {
          // Check distance from last notification
          const dist = Math.sqrt(
            Math.pow((current.lat - lastNotifLocation.current.lat) * 111320, 2) +
            Math.pow((current.lng - lastNotifLocation.current.lng) * 111320 * Math.cos(current.lat * Math.PI / 180), 2)
          );
          if (dist >= threshold) shouldSend = true;
        }

        if (shouldSend && latestUser.profileSetupDone !== false) {
          lastNotifLocation.current = { lat: current.lat, lng: current.lng };
          try { sessionStorage.setItem('ourhertz_last_notif_loc', JSON.stringify({ lat: current.lat, lng: current.lng })); } catch {}
          // Send connection notification message
          import('../../services/messageService').then(({ sendMessage }) => {
            sendMessage({
              text: `${latestUser.nickname}${i18n.language === 'ko' ? '님이 접속했습니다.' : ' has connected.'}`,
              senderId: latestUser.uid,
              senderNickname: latestUser.nickname,
              gender: latestUser.gender,
              profileImageUrl: latestUser.profileImageUrl,
              lat: current.lat,
              lng: current.lng,
              radius: useLocationStore.getState().radarRadius,
              isBot: false,
              isNearbyNotification: true,
              manualFreqApplied: useLocationStore.getState().isManualFreq,
              senderEmail: latestUser.email,
              isGuest: latestUser.isGuest,
            }).catch(() => {});
          });
        }
      }
    };

    if (loc) syncLocation();
    const intervalMs = (syncIntervalSec || 10) * 1000;
    const interval = setInterval(syncLocation, intervalMs);

    // Teleport → fire sync/notification immediately (don't wait for next tick).
    const onTeleport = () => { syncLocation(); };
    window.addEventListener('ourhertz-teleported', onTeleport);

    return () => {
      clearInterval(interval);
      window.removeEventListener('ourhertz-teleported', onTeleport);
    };
  }, [user?.uid, user?.nickname, syncIntervalSec]);

  // Mark offline only on logout / AppLayout unmount — NOT on syncIntervalSec or nickname change.
  // Previously this was part of the location-sync effect, which caused isOnline=false to race
  // with updateMyLocation whenever those deps changed (leaving web users stuck as gray).
  // NOTE: client-side disconnect notifications were removed — iOS process kill doesn't run
  // pagehide/cleanup reliably, so it can't be delivered consistently. Proper implementation
  // needs a server-side heartbeat + Cloud Function, to be added when the feature is revisited.
  useEffect(() => {
    if (!user) return;
    const uid = user.uid;
    return () => {
      goOffline(uid).catch(() => {});
    };
  }, [user?.uid]);

  // Subscribe to unread DM count + web notification
  const prevUnreadRef = useRef(-1); // -1 = initial load, skip notification
  useEffect(() => {
    if (!user) return;
    prevUnreadRef.current = -1;
    const unsub = subscribeUnreadCount(user.uid, (count) => {
      if (prevUnreadRef.current >= 0 && count > prevUnreadRef.current) {
        // Check master push toggle + DM notification setting
        const currentUser = useAuthStore.getState().user;
        const dmEnabled = currentUser?.pushSettings?.dmNotifications !== false;
        if (dmEnabled) {
          import('../../services/webPushService').then(({ sendWebNotification }) => {
            sendWebNotification(
              'OurHertz',
              i18n.language === 'ko' ? `새 쪽지 ${count}건` : `${count} new messages`,
              { tag: 'dm-unread', onClick: () => { window.location.pathname = '/dm'; } }
            );
          });
        }
      }
      prevUnreadRef.current = count;
      setUnreadCount(count);
    });
    return unsub;
  }, [user?.uid]);

  // Chat message notifications + unread badge
  const locationRef = useRef(location);
  useEffect(() => { locationRef.current = location; }, [location]);
  // Reset unread count when navigating to radio page or when user catches up
  useEffect(() => {
    if (location.pathname === '/app') setUnreadMsgCount(0);
  }, [location.pathname]);
  useEffect(() => {
    const onReading = () => setUnreadMsgCount(0);
    window.addEventListener('radio-reading', onReading);
    return () => window.removeEventListener('radio-reading', onReading);
  }, []);
  useEffect(() => {
    if (!user) return;
    let unsub: (() => void) | undefined;
    import('../../services/messageService').then(({ subscribeMessages }) => {
      let initialLoad = true;
      unsub = subscribeMessages((msgs: any[], newMsgIds: Set<string>) => {
        if (initialLoad) { initialLoad = false; return; }
        if (newMsgIds.size === 0) return;

        // Filter new messages by radar radius
        const radarMeters = useLocationStore.getState().radarRadius * 1000;
        const myLoc = useLocationStore.getState().effectiveLocation;
        // Helper: haversine distance in meters
        const calcDist = (lat1: number, lng1: number, lat2: number, lng2: number) => {
          const R = 6_371_000;
          const toRad = (d: number) => (d * Math.PI) / 180;
          const dLat = toRad(lat2 - lat1);
          const dLng = toRad(lng2 - lng1);
          const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
          return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        };

        const newInRange: Array<any & { _dist?: number }> = [];
        const blockedSet = new Set(user.blockedUserIds ?? []);
        // Read live so toggling the setting takes effect without re-subscribing.
        const hideBots = useAuthStore.getState().user?.rejectBotMessages === true;
        for (const m of msgs) {
          if (!newMsgIds.has(m.id) || m.senderId === user.uid) continue;
          if (blockedSet.has(m.senderId)) continue;
          // "AI Bot 표시" off → bot messages raise no badge and no notification.
          if (hideBots && m.isBot) continue;
          // Must have both my location and message location to filter
          if (!myLoc || m.lat == null || m.lng == null) continue;
          const dist = calcDist(myLoc.lat, myLoc.lng, m.lat, m.lng);
          if (dist > radarMeters) continue;
          newInRange.push({ ...m, _dist: dist });
        }

        if (newInRange.length === 0) return;

        // Check if user is actively reading the radio chat
        const onRadioPage = locationRef.current.pathname === '/app';
        const radioNotReading = (window as any).__ourhertz_radio_not_reading === true;
        const shouldBadge = !onRadioPage || radioNotReading;

        // Update unread badge
        if (shouldBadge) {
          setUnreadMsgCount(prev => prev + newInRange.length);
        }

        // Browser notification: only when browser tab is NOT focused
        console.log(`[Notif] ${newInRange.length} in range, hasFocus=${document.hasFocus()}, radar=${radarMeters}m, myLoc=${!!myLoc}`);
        if (!document.hasFocus()) {
          // Read latest user settings to respect toggle changes
          const latestSettings = useAuthStore.getState().user?.pushSettings;
          const lastNew = newInRange[newInRange.length - 1];
          if (lastNew && 'Notification' in window && Notification.permission === 'granted') {
            const isNearby = lastNew.isNearbyNotification === true;
            // Check per-type notification setting
            if (isNearby && latestSettings?.nearbyNotifications !== true) return;
            if (!isNearby && latestSettings?.messageNotifications === false) return;

            const isKo = i18n.language === 'ko';
            const dist = lastNew._dist ?? 0;
            const distStr = dist >= 1000 ? `${Math.round(dist / 1000)}km` : `${Math.round(dist)}m`;
            const title = `${lastNew.senderNickname ?? 'OurHertz'} (${distStr})`;
            const body = isNearby ? (isKo ? '접속했습니다' : 'Connected') : (lastNew.text?.slice(0, 50) ?? '');
            const n = new Notification(title, { body, icon: '/favicon.ico', tag: 'chat-message', silent: false });
            n.onclick = () => { window.focus(); n.close(); };
            setTimeout(() => n.close(), 5000);
          }
        }
      });
    });
    return () => { unsub?.(); };
  }, [user?.uid]);

  // Request web notification permission on mount (re-request if still 'default')
  useEffect(() => {
    if ('Notification' in window && Notification.permission !== 'denied') {
      import('../../services/webPushService').then(({ requestWebPushPermission }) => requestWebPushPermission());
    }
  }, []);

  // === Listen for custom events from child components ===
  useEffect(() => {
    const handleOpenProfile = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setProfileModal({ uid: detail.uid, nickname: detail.nickname });
    };
    const handleOpenDM = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setDmCompose({ recipientId: detail.recipientId, recipientNickname: detail.recipientNickname });
    };
    const handleOpenReport = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setReportModal({ targetId: detail.targetId, targetNickname: detail.targetNickname, contentType: detail.contentType, contentPreview: detail.contentPreview, contentTimestamp: detail.contentTimestamp });
    };

    const handleOpenDrop = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setDropDetailId(detail.dropId);
    };

    const handleOpenInfo = (e: Event) => setInfoPost((e as CustomEvent).detail.post);

    window.addEventListener('open-profile', handleOpenProfile);
    window.addEventListener('open-dm-compose', handleOpenDM);
    window.addEventListener('open-report', handleOpenReport);
    window.addEventListener('open-drop-detail', handleOpenDrop);
    window.addEventListener('open-info-post', handleOpenInfo);

    return () => {
      window.removeEventListener('open-profile', handleOpenProfile);
      window.removeEventListener('open-dm-compose', handleOpenDM);
      window.removeEventListener('open-report', handleOpenReport);
      window.removeEventListener('open-drop-detail', handleOpenDrop);
      window.removeEventListener('open-info-post', handleOpenInfo);
    };
  }, []);

  return (
    <div className="safe-top flex h-full flex-col" style={{ backgroundColor: currentTheme.appBg }}>
      <LandscapeGuard />
      <main className="min-h-0 flex-1 overflow-hidden">
        <Outlet key={chatTheme} />
      </main>

      {/* Global toolbar: Language + Battery + Font */}
      <div className="flex shrink-0 flex-wrap items-center gap-y-0.5 px-2 py-0.5"
        style={{ backgroundColor: currentTheme.headerBg, borderTop: `1px solid ${currentTheme.borderColor}` }}>
        {/* Main row: Language + Theme + Battery + Info — always one line */}
        <div className="flex min-w-0 flex-auto items-center gap-1.5">
          {/* Language */}
          <button onClick={() => {
            const newLang = i18n.language === 'ko' ? 'en' : 'ko';
            i18n.changeLanguage(newLang);
            useUIStore.getState().setLanguage(newLang as any);
            if (user) import('../../services/userService').then(({ updateUser: uu }) => uu(user.uid, { language: newLang } as any)).catch(() => {});
          }}
            className="flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-[0.6875rem] font-bold"
            style={{ backgroundColor: colors.card, color: colors.textMuted, border: `1px solid ${colors.border}` }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            {i18n.language === 'ko' ? 'EN' : '한'}
          </button>

          {/* Theme toggle */}
          <button onClick={() => {
            const ts = useThemeStore.getState();
            const idx = THEMES.findIndex(t => t.key === ts.chatTheme);
            ts.setChatTheme(THEMES[(idx + 1) % THEMES.length].key);
          }}
            className="flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-[0.6875rem] font-bold"
            style={{ backgroundColor: colors.card, color: colors.textMuted, border: `1px solid ${colors.border}` }}>
            🎨 {(() => { const t = THEMES.find(t => t.key === chatTheme); return i18n.language === 'ko' ? t?.label.ko : t?.label.en; })()}
          </button>

          {/* Battery - center + info icon */}
          <div className="flex flex-1 items-center justify-center gap-2">
            <div className="relative flex items-center rounded border px-0.5 py-px" style={{ borderColor: batteryColor, width: 24, height: 12 }}>
              {[0,1,2,3].map(i=><div key={i} className="mx-px rounded-sm" style={{ width:3, height:7, backgroundColor: i<filledBars ? batteryColor : colors.surface }}/>)}
              <div className="absolute rounded-r-sm" style={{ right:-3, width:2, height:4, backgroundColor: batteryColor }}/>
            </div>
            <span className="text-xs font-bold" style={{ color: batteryColor, textShadow: isOvercharged ? `0 0 6px ${batteryColor}` : 'none' }}>{pct}%</span>
            <span className="font-lcd text-xs font-bold" style={{ color: colors.primary }}>{cells.toLocaleString()}</span>
            {/* Info icon */}
            <button onClick={async () => {
              const lang = i18n.language === 'ko' ? 'ko' : 'en';
              const { getInfoIconPost } = await import('../../services/boardService');
              const post = await getInfoIconPost(lang);
              if (post) { window.dispatchEvent(new CustomEvent('open-info-post', { detail: { post } })); }
            }}
              className="ml-2 flex h-5 w-5 items-center justify-center rounded-full"
              style={{ backgroundColor: colors.info + '20', border: `1px solid ${colors.info}40` }}>
              <span className="text-[0.5625rem] font-bold" style={{ color: colors.info }}>i</span>
            </button>
          </div>
        </div>

        {/* Font — wraps to next line when narrow */}
        <div className="flex shrink-0 items-center gap-0.5 pl-1.5">
          <button onClick={() => useUIStore.getState().decreaseFontLevel()}
            className="flex h-6 w-6 items-center justify-center rounded text-[0.625rem] font-bold"
            style={{ backgroundColor: colors.card, color: colors.textMuted, border: `1px solid ${colors.border}` }}>A-</button>
          <button onClick={() => useUIStore.getState().increaseFontLevel()}
            className="flex h-6 w-6 items-center justify-center rounded text-[0.625rem] font-bold"
            style={{ backgroundColor: colors.card, color: colors.textMuted, border: `1px solid ${colors.border}` }}>A+</button>
        </div>
      </div>

      <nav
        className="safe-bottom flex shrink-0"
        style={{
          backgroundColor: colors.header,
          borderTop: `1px solid ${colors.border}`,
        }}
      >
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.to === '/app'}
            className="relative flex flex-1 flex-col items-center py-1.5"
            onClick={() => {
              if (location.pathname === tab.to || (tab.to === '/app' && location.pathname === '/app')) {
                window.dispatchEvent(new CustomEvent('tab-reclick', { detail: { tab: tab.to } }));
              }
            }}
          >
            {({ isActive }) => {
              const color = isActive ? colors.primary : colors.textMuted;
              return (
                <>
                  <div className="relative">
                    {tab.icon({ color })}
                    {/* DM unread badge */}
                    {tab.to === '/dm' && unreadCount > 0 && (
                      <span className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                    {/* Radio unread message badge */}
                    {tab.to === '/app' && unreadMsgCount > 0 && (
                      <span className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold text-white"
                        style={{ backgroundColor: colors.primary }}>
                        {unreadMsgCount > 99 ? '99+' : unreadMsgCount}
                      </span>
                    )}
                  </div>
                  <span
                    className="mt-0.5 text-center text-[10px] font-semibold leading-tight"
                    style={{ color }}
                  >
                    {t(tab.label)}
                  </span>
                </>
              );
            }}
          </NavLink>
        ))}
      </nav>

      {/* === Global Context Menu === */}
      <UserContextMenu />

      {/* === Global Modals === */}
      {profileModal && (
        <UserProfileModal
          uid={profileModal.uid}
          nickname={profileModal.nickname}
          onClose={() => setProfileModal(null)}
          onSendDM={(id, nick) => {
            setProfileModal(null);
            setDmCompose({ recipientId: id, recipientNickname: nick });
          }}
          onReport={(id, nick) => {
            setProfileModal(null);
            setReportModal({ targetId: id, targetNickname: nick });
          }}
        />
      )}

      {dmCompose && (
        <DMComposeModal
          recipientId={dmCompose.recipientId}
          recipientNickname={dmCompose.recipientNickname}
          onClose={() => setDmCompose(null)}
        />
      )}

      {reportModal && (
        <ReportModal
          targetId={reportModal.targetId}
          targetNickname={reportModal.targetNickname}
          contentType={reportModal.contentType}
          contentPreview={reportModal.contentPreview}
          contentTimestamp={reportModal.contentTimestamp}
          onClose={() => setReportModal(null)}
        />
      )}

      {dropDetailId && (
        <DropDetailModal dropId={dropDetailId} onClose={() => setDropDetailId(null)} />
      )}

      {/* Info post modal */}
      {infoPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setInfoPost(null)}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl"
            style={{ backgroundColor: colors.surface, border: `2px solid ${colors.info}` }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${colors.border}`, backgroundColor: `${colors.info}10` }}>
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full" style={{ backgroundColor: colors.info + '30' }}>
                  <span className="text-xs font-bold" style={{ color: colors.info }}>i</span>
                </span>
                <h3 className="text-sm font-bold" style={{ color: colors.text }}>{infoPost.title}</h3>
              </div>
              <button onClick={() => setInfoPost(null)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.textMuted} strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="max-h-72 overflow-y-auto px-4 py-3">
              <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: colors.text }}>{infoPost.content}</p>
            </div>
            <div className="px-4 py-3 text-right" style={{ borderTop: `1px solid ${colors.border}` }}>
              <button onClick={() => setInfoPost(null)} className="rounded-lg px-4 py-2 text-sm font-bold text-white" style={{ backgroundColor: colors.primary }}>
                {i18n.language === 'ko' ? '확인' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notice/Release Note popup - re-check on every tab change */}
      {showGoogleSetup && user && (
        <GoogleProfileSetup onComplete={() => setShowGoogleSetup(false)} />
      )}
      {user && !showGoogleSetup && <NoticePopup />}

      {/* Location consent modal — shown before system permission prompt */}
      {showLocationConsent && (
        <LocationConsentModal
          lang={i18n.language}
          onAccept={() => {
            setShowLocationConsent(false);
            const store = useLocationStore.getState();
            store.setUserOptedOut(false);
            store.requestPermission().then((granted) => {
              if (granted) {
                startWatching();
                // Chain the map-visibility opt-in prompt (Apple 5.1.2).
                maybeAskMapConsent();
              }
            });
          }}
          onDecline={() => {
            setShowLocationConsent(false);
            // Persist opt-out so the modal doesn't reappear and location is not watched
            useLocationStore.getState().setUserOptedOut(true);
          }}
        />
      )}

      {/* Map-visibility consent modal — Apple 5.1.2 opt-in; default is hidden */}
      {showMapLocationConsent && (
        <MapLocationConsentModal
          lang={i18n.language}
          onShow={(dontAsk) => {
            setShowMapLocationConsent(false);
            const store = useLocationStore.getState();
            store.setShareLocationOnMap(true);
            store.setShareLocationPrompted(true);
            if (dontAsk) store.setDontAskMapConsent(true);
          }}
          onHide={(dontAsk) => {
            setShowMapLocationConsent(false);
            const store = useLocationStore.getState();
            store.setShareLocationOnMap(false);
            store.setShareLocationPrompted(true);
            if (dontAsk) store.setDontAskMapConsent(true);
            // Defensive: if a stale lat/lng lingers on the user doc, clear it now.
            const uid = useAuthStore.getState().user?.uid;
            if (uid) import('../../services/locationService').then(({ clearMyLocationOnMap }) => clearMyLocationOnMap(uid)).catch(() => {});
          }}
        />
      )}
    </div>
  );
}
